import { trace } from 'rytm-helpers'
import { TweenMax, TimelineMax, Power0, Power1, Power2, Power3, Power4, Back, Bounce, Elastic, Expo, Sine } from 'gsap'
import SoundController from './SoundController'
import KeyboardController from './KeyboardController'
import VisualStage from '../models/VisualStage'
import { rand, relPositionX } from '../lib/math_helpers'


const FPTE = 30 // frames per time entity (sound time / FPTE)
const RANDOM_SND_BANK_F = .5 // chances for randomizing sound bank on each bar
const RANDOM_PAUSE = .1 // chances for PAUSE
const RANDOM_VARIOUS = .2 // chances for variouse shape
const RANDOM_MOUSE_PATH = .5 // chances for mouse path
const PAUSE_TIME_MIN = 300
const PAUSE_TIME_MAX = 5500

class SequenceController {
  /**
  * SequenceController class constructor  
  * @constructor
  **/
  constructor() {
    this.prepareSoundController()
    this.prepareVisualStage()
    this.keyController = new KeyboardController()
    this._frame = 0
    this._playbackCounter = 0
    this._mousePressed = false
    this.mouseP = {x: window.innerWidth * .5,y: window.innerHeight * .5}
  }
  prepareVisualStage() {
    const params = {
      onNextFrame: this.onSequenceNextFrame.bind(this),
      onNextBar: this.onSequenceNextBar.bind(this),
    }
    this.visualStage = new VisualStage(params)
  }
  prepareSoundController() {
    if (window.__wa_soundbanks) {
      this.sndController = new SoundController();
      this.sndController.addSoundBanks(window.__wa_soundbanks)
    } else {
      console.warn("WA soundbank is missing :(")
    }
  }
  /**
   * sequence START
   */
  sequenceStart() {
    trace("sequence start")
    if (this.visualStage) {
      const wrapper = document.querySelector(".editionhome-playground .visual-wrapper")
      if (wrapper) {
        this.visualStage.init(wrapper)
      } else {
        console.warn("Visual Stage wrapper missing :(")
      }
    }
  }
  /**
   * sequence STOP
   */
  sequenceStop() {
    trace("sequence stop")
    this.keyController.destroy()
    if (this.visualStage) {
      this.visualStage.destroy()
    }
    if (this.sndController) {
      this.sndController.destroySoundBanks()
    }
  }
  /**
   * on window resize
   */
  onWindowResize() {
    if (this.visualStage) {
      this.visualStage.onWindowResize()
    }
  }
  /**
   * on key down
   */
  onKeyDown(e) {
    this.keyController.onKeyDown(e, this._frame)
  }
  /**
   * on mouse down
   */
  onMouseDown(mouseP) {
    this._mousePressed = true
    this.mouseP = mouseP
    if (this.visualStage) {
      this.visualStage.onMouseDown(mouseP)
    }
    if (this.sndController) {
      this.sndController.onMouseDown(mouseP)
    }
  }
  /**
   * on mouse up
   */
  onMouseUp(mouseP) {
    this._mousePressed = false
    this.mouseP = mouseP
    if (this.visualStage) {
      this.visualStage.onMouseUp(mouseP)
    }
    if (this.sndController) {
      this.sndController.onMouseUp(mouseP)
    }
    this.keyController.randomKey(this._frame)
  }
  /**
   * on mouse move
   */
  onMouseMove(mouseP) {
    this.mouseP = mouseP
    if (this.visualStage) {
      this.visualStage.onMouseMove(mouseP)
    }
    if (this.sndController) {
      this.sndController.onMouseMove(mouseP)
    }
  }
  // when visual calls NEXT FRAME
  onSequenceNextFrame() {
    // trace("sequence next frame", this._playbackCounter)
    this._frame++
    if (this._playbackCounter == 0) {
      // check keyboard
      if (this.keyController.isKeyboardOn(this._frame)) {
        // keyboard sequence
        const letter = this.keyController.currentLetter
        if (letter) {
          this._playByKeyboard(letter)
          this.keyController.resetLetter()
        } 
      } else {
        // regular automated sequence
        if (!this._randomize(RANDOM_PAUSE)) {
          this._play()
        } else {
          this._pause()
        }
      }
    } else {
      this._playbackCounter--
    }
    // mouse path?
    // if (this._randomize(RANDOM_MOUSE_PATH)) {
    //   this._mousePath()
    // }
  }
  // when visual calls NEXT BAR
  onSequenceNextBar() {
    if (this.sndController) {
      // play music
      this.sndController.playRandomMusic(10)
      // randomize 
      if (this._randomize(RANDOM_SND_BANK_F)) {
        this.sndController.randomizeSoundBank()
      }
    }
  }



  /**
   * ### PRIVATE METHODS ###
   */
  _randomize(f) {
    const max = 1000
    const pom = rand(1,max)
    return pom < max * f
  }

  _playByKeyboard(letter) {
    const sndEvent = this.sndController.playSoundByLetter(letter)
    if (sndEvent.id) {
      this._onSoundPlay(sndEvent)
    } else {
      //a no sound was found...
      this._play()
    }
  }
  _play() {
    const sndEvent = this.sndController.playRandomSound()
    this._onSoundPlay(sndEvent)
  }
  _various() {
    const sndEvent = null
    this.visualStage.nextVarious(sndEvent)
    if (this.sndController) {
      this.sndController.playRandomMusic(rand(50,400))
    }
  }
  _pause() {
    const time = this._getSequencePauseTime()
    this._playbackCounter = Math.round(time / this._getCurrentFpte())
    if (this.visualStage) {
      this.visualStage.nextPause(time)
    }
  }
  _mousePath() {
    if (this.visualStage) {
      this.visualStage.nextMousePath()
    }
  }
  /**
   * 
   * @param {SoundEvent} sndEvent
   */
  _onSoundPlay(sndEvent) {
    this._playbackCounter = Math.round(sndEvent.time / this._getCurrentFpte())
    if (this.visualStage) {
      if (this._randomize(RANDOM_VARIOUS)) {
        this._various()
      } else {
        this.visualStage.nextEntity(sndEvent)
      }
    }
  }
  
  
  /**
   * get sequence PAUSE random time
   */
  _getSequencePauseTime() {
    const min = PAUSE_TIME_MIN
    const max = PAUSE_TIME_MAX
    return Math.random() * (max - min) + min
  }

  _getCurrentFpte() {
    let currentFpte = FPTE
    if (this.mouseP && this.mouseP.y) {
      const mY = relPositionX(this.mouseP.y)
      const f = 50
      currentFpte = FPTE - mY * f + (f * .5)
    }
    return currentFpte
  }
  

}

export default SequenceController;
